import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import {
  Box,
  HStack,
  Text,
  IconButton,
  Select,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  VStack,
  Button,
  useBreakpointValue,
} from '@chakra-ui/react'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import { FileItem } from '@/types'
import apiService from '@/services/api'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

type LoadedPdf = Parameters<NonNullable<React.ComponentProps<typeof Document>['onLoadSuccess']>>[0]

interface PDFViewerProps {
  file: FileItem
  scale: number
  onScaleChange: (scale: number) => void
}

/** Match react-pdf Page scale logic (width + scale props). */
async function computePageHeights(
  pdf: LoadedPdf,
  numPages: number,
  width: number | undefined,
  scale: number,
): Promise<number[]> {
  const heights: number[] = []
  for (let i = 1; i <= numPages; i++) {
    const page = await pdf.getPage(i)
    const rotate = page.rotate
    const viewport1 = page.getViewport({ scale: 1, rotation: rotate as number })
    let pageScale = 1
    if (width) {
      pageScale = width / viewport1.width
    }
    const finalScale = scale * pageScale
    const viewport = page.getViewport({ scale: finalScale, rotation: rotate as number })
    heights.push(viewport.height)
  }
  return heights
}

type PdfPageRowProps = {
  pageNumber: number
  scale: number
  pageWidth: number | undefined
  minHeight: number
  scrollRoot: HTMLDivElement | null
  rootMarginPx: number
  pageRefs: React.MutableRefObject<Map<number, HTMLDivElement>>
}

const PdfPageRow = memo(function PdfPageRow({
  pageNumber,
  scale,
  pageWidth,
  minHeight,
  scrollRoot,
  rootMarginPx,
  pageRefs,
}: PdfPageRowProps) {
  const [showPage, setShowPage] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  const setWrapperRef = useCallback(
    (node: HTMLDivElement | null) => {
      wrapperRef.current = node
      if (node) {
        pageRefs.current.set(pageNumber, node)
      } else {
        pageRefs.current.delete(pageNumber)
      }
    },
    [pageNumber, pageRefs],
  )

  useEffect(() => {
    const el = wrapperRef.current
    const root = scrollRoot
    if (!root || !el) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowPage(entry.isIntersecting)
      },
      {
        root,
        rootMargin: `${rootMarginPx}px 0px`,
        threshold: 0,
      },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [scrollRoot, rootMarginPx])

  return (
    <Box
      ref={setWrapperRef}
      id={`pdf-page-${pageNumber}`}
      data-pdf-page={pageNumber}
      minH={`${minHeight}px`}
      borderRadius="md"
      overflow="hidden"
      bg="white"
      boxShadow="sm"
      aria-label={`Page ${pageNumber}`}
    >
      {showPage ? (
        <Page
          pageNumber={pageNumber}
          scale={scale}
          width={pageWidth}
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />
      ) : (
        <Box minH={`${minHeight}px`} bg="gray.100" aria-hidden />
      )}
    </Box>
  )
})

export default function PDFViewer({ file, scale, onScaleChange: _onScaleChange }: PDFViewerProps) {
  const [numPages, setNumPages] = useState<number | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pdfDoc, setPdfDoc] = useState<LoadedPdf | null>(null)
  const [pageHeights, setPageHeights] = useState<number[] | null>(null)
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null)
  const [rootMarginPx, setRootMarginPx] = useState(600)

  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const scrollRaf = useRef<number | null>(null)

  const isMobile = useBreakpointValue({ base: true, md: false })
  const pageWidth = isMobile ? undefined : 800

  useEffect(() => {
    pageRefs.current.clear()
    setIsLoading(true)
    setError(null)
    setPdfDoc(null)
    setPageHeights(null)
    setNumPages(null)
    const url = apiService.getFilePreviewUrl(file.id)
    setPdfUrl(url)
    setIsLoading(false)
  }, [file])

  const onDocumentLoadSuccess = useCallback((pdf: LoadedPdf) => {
    setPdfDoc(pdf)
    setNumPages(pdf.numPages)
    setPageNumber(1)
    setIsLoading(false)
  }, [])

  const onDocumentLoadError = useCallback((err: Error) => {
    setError(err.message)
    setIsLoading(false)
  }, [])

  useEffect(() => {
    if (!pdfDoc || !numPages) {
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const heights = await computePageHeights(pdfDoc, numPages, pageWidth, scale)
        if (!cancelled) {
          setPageHeights(heights)
        }
      } catch {
        if (!cancelled) {
          setPageHeights(null)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [pdfDoc, numPages, pageWidth, scale])

  useEffect(() => {
    const el = scrollRoot
    if (!el) {
      return
    }
    const update = () => {
      setRootMarginPx(Math.round(el.clientHeight * 1.5))
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [scrollRoot])

  const syncPageFromScroll = useCallback(() => {
    const container = scrollRoot
    if (!container || !numPages) {
      return
    }
    const rootRect = container.getBoundingClientRect()
    const centerY = rootRect.top + rootRect.height / 2
    for (let p = 1; p <= numPages; p++) {
      const el = pageRefs.current.get(p)
      if (!el) {
        continue
      }
      const r = el.getBoundingClientRect()
      if (r.top <= centerY && r.bottom >= centerY) {
        setPageNumber((prev) => (prev !== p ? p : prev))
        return
      }
    }
  }, [scrollRoot, numPages])

  const onScrollContainerScroll = useCallback(() => {
    if (scrollRaf.current != null) {
      return
    }
    scrollRaf.current = window.requestAnimationFrame(() => {
      scrollRaf.current = null
      syncPageFromScroll()
    })
  }, [syncPageFromScroll])

  useEffect(() => {
    const el = scrollRoot
    if (!el) {
      return
    }
    el.addEventListener('scroll', onScrollContainerScroll, { passive: true })
    syncPageFromScroll()
    return () => {
      el.removeEventListener('scroll', onScrollContainerScroll)
      if (scrollRaf.current != null) {
        window.cancelAnimationFrame(scrollRaf.current)
        scrollRaf.current = null
      }
    }
  }, [scrollRoot, onScrollContainerScroll, syncPageFromScroll])

  const scrollToPage = useCallback((p: number) => {
    const el = pageRefs.current.get(p)
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [])

  const goToPrevPage = () => {
    const next = Math.max(1, pageNumber - 1)
    scrollToPage(next)
  }

  const goToNextPage = () => {
    const next = Math.min(numPages || 1, pageNumber + 1)
    scrollToPage(next)
  }

  const handlePageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const p = parseInt(e.target.value, 10)
    scrollToPage(p)
  }

  const pageIndices = useMemo(
    () => (numPages ? Array.from({ length: numPages }, (_, i) => i + 1) : []),
    [numPages],
  )

  if (isLoading) {
    return (
      <Center h="full">
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.500" />
          <Text color="gray.500">Loading PDF...</Text>
        </VStack>
      </Center>
    )
  }

  if (error) {
    return (
      <Center h="full">
        <Alert status="error" maxW="md" borderRadius="lg">
          <AlertIcon />
          <VStack align="start" spacing={2}>
            <Text fontWeight="semibold">Failed to load PDF</Text>
            <Text fontSize="sm">{error}</Text>
            <Button
              size="sm"
              onClick={() => {
                setIsLoading(true)
                setError(null)
                setPdfUrl(apiService.getFilePreviewUrl(file.id))
                setIsLoading(false)
              }}
            >
              Retry
            </Button>
          </VStack>
        </Alert>
      </Center>
    )
  }

  const heightsReady = pageHeights && pageHeights.length === (numPages ?? 0)

  return (
    <Box h="full" display="flex" flexDirection="column">
      {numPages && numPages > 1 && (
        <HStack
          spacing={2}
          px={4}
          py={2}
          bg="white"
          borderBottom="1px solid"
          borderColor="gray.100"
          justify="center"
          flexShrink={0}
        >
          <IconButton
            aria-label="Previous page"
            icon={<FiChevronLeft />}
            size="sm"
            variant="ghost"
            isDisabled={pageNumber <= 1}
            onClick={goToPrevPage}
          />
          {isMobile ? (
            <Text fontSize="sm" color="gray.600">
              {pageNumber} / {numPages}
            </Text>
          ) : (
            <HStack spacing={1}>
              <Select size="sm" value={pageNumber} onChange={handlePageChange} w="70px">
                {Array.from({ length: numPages || 0 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </Select>
              <Text fontSize="sm" color="gray.500">
                of {numPages}
              </Text>
            </HStack>
          )}
          <IconButton
            aria-label="Next page"
            icon={<FiChevronRight />}
            size="sm"
            variant="ghost"
            isDisabled={pageNumber >= (numPages || 1)}
            onClick={goToNextPage}
          />
        </HStack>
      )}

      <Box
        ref={setScrollRoot}
        flex={1}
        overflow="auto"
        display="flex"
        justifyContent="center"
        alignItems="flex-start"
        p={4}
        bg="gray.50"
        className="ph-no-capture file-content-private"
        data-private="true"
      >
        {pdfUrl && (
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <Center h="400px">
                <Spinner size="xl" color="brand.500" />
              </Center>
            }
          >
            {!heightsReady || !scrollRoot ? (
              <Center minH="400px" w="full">
                <Spinner size="xl" color="brand.500" />
              </Center>
            ) : (
              <VStack spacing={4} align="stretch" maxW="100%">
                {pageIndices.map((p, idx) => (
                  <PdfPageRow
                    key={p}
                    pageNumber={p}
                    scale={scale}
                    pageWidth={pageWidth}
                    minHeight={pageHeights![idx]}
                    scrollRoot={scrollRoot}
                    rootMarginPx={rootMarginPx}
                    pageRefs={pageRefs}
                  />
                ))}
              </VStack>
            )}
          </Document>
        )}
      </Box>
    </Box>
  )
}
